import { Component, Input, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosIcon } from '@common/config/environment.interface';
import { EidosUtilityService } from '@common/services/eidos-utility.service';
import { EidosIconType } from '../../models/core-constant.model';
import { EidosBaseComponent } from '../eidos-base.component';
import { takeUntil } from 'rxjs';

export type EidosIconTooltipPosition = 'up' | 'down' | 'left' | 'right';

const DEFAULT_SVG_ICONS: Record<string, string> = {
  logo_square_1c: 'assets/svg/eidos_logo_square_1colore.svg',
  logo_square_2c: 'assets/svg/eidos_logo_square_2colori.svg',
  logo_wide_1c: 'assets/svg/eidos_logo_wide_1colore.svg',
  logo_wide_2c: 'assets/svg/eidos_logo_wide_2colori.svg',
  'logo_4.0_1c': 'assets/svg/eidos4.0_logo_wide_1colore.svg',
  'logo_4.0n_1c': 'assets/svg/eidos4.0new_logo_wide_1colore.svg',
  'logo_4.0n_1c_v2': 'assets/svg/eidos4.0new_logo_wide_1colore_v2.svg',
  'logo-ak': 'assets/svg/logo-ak.svg',
};

@Component({
  selector: 'eidos-icon',
  templateUrl: './eidos-icon.component.html',
  styleUrls: ['./eidos-icon.component.scss']
})
export class EidosIconComponent extends EidosBaseComponent implements OnInit {

  EidosIconType = EidosIconType;

  private svgUrlByCode: Record<string, string> = { ...DEFAULT_SVG_ICONS };

  /**
   * Icon configuration
   *
   * @type {(IEidosIcon | null)}
   * @memberof EidosIconComponent
   */
  @Input() set iconConfiguration(value: IEidosIcon | null) {
    this._iconConfiguration = value;
    this.setMyBizIcon();
  }

  get iconConfiguration(): IEidosIcon | null {
    return this._iconConfiguration;
  }

  private _iconConfiguration: IEidosIcon | null = null;

  /**
   * Icon tooltip
   *
   * @type {string}
   * @memberof EidosIconComponent
   */
  @Input('tooltip')
  public iconTooltip: string | undefined;
  /**
   * Position of the tooltip
   *
   * @memberof EidosIconComponent
   */
  @Input('tooltipPosition')
  public iconTooltipPosition: EidosIconTooltipPosition = "down";
  /**
   * Icon badge real value
   *
   * @type {(string | number | undefined)}
   * @memberof EidosIconComponent
   */
  @Input('badge')
  public _iconBadge: string | number | undefined;
  /**
   * Icon badge displayed value
   * (used to handle 0 as undefined)
   *
   * @readonly
   * @type {(number | undefined)}
   * @memberof EidosIconComponent
   */
  public get iconBadge(): string | number | undefined {
    return !this._iconBadge ? undefined : this._iconBadge;
  }
  /**
   * Icon clickable flag
   *
   * @type {(boolean | undefined)}
   * @memberof EidosIconComponent
   */
  @Input('clickable')
  public iconClickable: boolean | undefined;
  /**
   * Icon rotation degrees
   *
   * @type {(number | undefined)}
   * @memberof EidosIconComponent
   */
  @Input('rotate')
  public iconRotate: number = 0;

  /**
   * Get icon transform (scale + rotation)
   *
   * @return {*}  {string}
   * @memberof EidosIconComponent
   */
  get iconScale(): string {
    const rotation = (this.iconRotate !== 0 ? this.iconRotate : 0);
    const scale = (this.iconConfiguration?.iconSize || 1);
    return `rotate(${rotation}deg) scaleX(${scale}) scaleY(${scale})`;
  }

  /**
  * Get Font Awesome class from icon config
  *
  * @return {*}  {string}
  * @memberof EidosIconComponent
  */
  getFAValue(): string {
    const raw = this.iconConfiguration?.iconValue ?? this.iconConfiguration?.iconCode ?? '';
    if (raw === 'fa-android' || raw === 'android') {
      return 'fab fa-android';
    }
    return raw.startsWith('fa') ? raw : `fa-${raw}`;
  }

  /**
   * Resolve SVG image URL (config map, iconUrl, or assets/svg/{code}.svg)
   *
   * @return {*}  {string}
   * @memberof EidosIconComponent
   */
  getSvgSrc(): string {
    const conf = this.iconConfiguration;
    if (!conf) {
      return '';
    }
    if (conf.iconUrl) {
      return conf.iconUrl;
    }
    const code = conf.iconValue ?? conf.iconCode ?? '';
    if (this.svgUrlByCode[code]) {
      return this.svgUrlByCode[code];
    }
    if (code.includes('/')) {
      return code;
    }
    return code ? `assets/svg/${code}.svg` : '';
  }

  /**
   * Get SVG icon size class
   *
   * @param {string} responsiveType
   * @return {*}  {string}
   * @memberof EidosIconComponent
   */
  getSvgScale(responsiveType: string): string {
    const size = this.iconConfiguration?.iconSize || 1;
    let zoomFactor = 1;
    switch (responsiveType) {
      case 'xs':
        zoomFactor = 3;
        break;
      case 'sm':
        zoomFactor = 1.5;
        break;
      default:
        zoomFactor = 1;
    }
    return 'eidos-icon-scale' + Math.ceil(size / zoomFactor);
  }

  constructor(
    private domSanitizer: DomSanitizer
    , private eidosUtilityService: EidosUtilityService
    , private eidosConfig: EidosConfigService
  ) {
    super();
    this.loadStandardIcons();
  }

  private loadStandardIcons(): void {
    this.eidosConfig.currentConfig
      .pipe(takeUntil(this.subscription))
      .subscribe({
        next: (cfg) => {
          this.svgUrlByCode = { ...DEFAULT_SVG_ICONS };
          if (!cfg.externalIcons?.length) {
            return;
          }
          cfg.externalIcons.forEach(icon => {
            const typedir = icon.type === 'svg' ? 'svg' : 'images';
            const url = icon.absoluteUrl ? icon.url : `assets/${typedir}/${icon.url}`;
            this.svgUrlByCode[icon.code] = url;
          });
        },
        error: () => {
          this.svgUrlByCode = { ...DEFAULT_SVG_ICONS };
        },
        complete: () => { }
      });
  }

  ngOnInit(): void {
    this.setMyBizIcon();
  }

  /**
   * Eventually set safe url of MyBiz icon
   *
   * @private
   * @memberof EidosIconComponent
   */
  private setMyBizIcon() {
    switch (this._iconConfiguration?.iconType) {
      case this.EidosIconType.SVG:
        if (this._iconConfiguration.iconUrl) {
          const code = this._iconConfiguration.iconCode ?? this._iconConfiguration.iconUrl.split('/').pop() ?? '';
          if (code) {
            this.svgUrlByCode[code] = this._iconConfiguration.iconUrl;
          }
        }
        break;
      case this.EidosIconType.MyBiz:
        if (this._iconConfiguration.iconCode) {
          this.eidosUtilityService.getMyBizImageUrl(this._iconConfiguration.iconCode).subscribe(url => {
            if (this._iconConfiguration) {
              this._iconConfiguration.iconCodeSafeUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(url);
            }
          })
        }
        break;
      default:
        break;
    }
  }
}
